import React, { memo, useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Trash2Fill } from '@lyearn/icons';
import { AlertDialog } from '@lyearn/molecules';
import { Checkbox, Stack, Typography, useSnackbar } from '@lyearn/ui';
import useResponse from '@/hooks/useResponse';
import { useDeleteClientMutation } from '@/modules/clients/graphql/mutations/generated/deleteClient';

import { DeleteClientModalProps } from './types';

function DeleteClient({ client, onClose, onSuccess }: DeleteClientModalProps) {
  const { t } = useTranslation('Clients');

  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();

  const [confirmation, setConfirmation] = useState(false);
  const [{ fetching }, deleteClient] = useDeleteClientMutation();

  const projectCount = client.projectCount;
  const clientId = client.id;
  const clientName = client.clientName;

  const onDelete = useCallback(async () => {
    const data = await deleteClient({
      data: {
        clientId,
      },
    });

    handleResponse({
      condition: !!data.data?.deleteClient?.success,
      onSuccess: () => {
        showSnackbar({
          message: t('Clients:ClientNameDeleted', { name: clientName }),
          startIcon: <Trash2Fill fillColorToken="icon-placeholder" />,
        });
        onClose();
        onSuccess?.(client);
      },
    });
  }, [
    deleteClient,
    clientId,
    handleResponse,
    showSnackbar,
    t,
    clientName,
    onClose,
    onSuccess,
    client,
  ]);

  return (
    <AlertDialog
      header={{
        title: t('Clients:DeleteClientDialog.Title'),
        description: t('Clients:DeleteClientDialog.Description'),
      }}
      primaryAction={{
        appearance: 'danger',
        label: t('Common:Actions.Delete'),
        onClick: onDelete,
        disabled: fetching || !confirmation,
        loading: fetching,
      }}
      onClose={onClose}>
      <Stack className="mt-20 px-8" direction="row" gap={8}>
        <Checkbox checked={confirmation} onChange={(e) => setConfirmation(e.target.checked)} />
        <Typography color="text-text-paragraph" variant="label-01">
          {t('Clients:DeleteClientDialog.Confirmation')}
        </Typography>
      </Stack>
    </AlertDialog>
  );
}

export default memo(DeleteClient);
